import { useState, useEffect } from 'react';
import { IonToast } from '@ionic/react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAUpdateToast() {
  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [installable, setInstallable] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;

    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        if (cancelled) return;
        if (navigator.serviceWorker.controller) setOfflineReady(true);

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setNeedRefresh(true);
            }
          });
        });
      })
      .catch(() => {
        /* SW registration not available in this context */
      });

    const onControllerChange = () => window.location.reload();
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallable(true);
      (window as unknown as { __deferredPrompt?: BeforeInstallPromptEvent }).__deferredPrompt = e as BeforeInstallPromptEvent;
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      window.removeEventListener('beforeinstallprompt', onPrompt);
    };
  }, []);

  const applyUpdate = () => {
    const regs = navigator.serviceWorker.getRegistrations();
    void regs.then((rs) => {
      for (const r of rs) {
        r.waiting?.postMessage({ type: 'SKIP_WAITING' });
      }
    });
  };

  const promptInstall = async () => {
    const prompt = (window as unknown as { __deferredPrompt?: BeforeInstallPromptEvent }).__deferredPrompt;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice;
    setInstallable(false);
    (window as unknown as { __deferredPrompt?: BeforeInstallPromptEvent }).__deferredPrompt = undefined;
  };

  return (
    <>
      <IonToast
        isOpen={offlineReady}
        message="BluePages is ready to work offline"
        duration={3000}
        color="success"
        onDidDismiss={() => setOfflineReady(false)}
      />
      <IonToast
        isOpen={needRefresh}
        message="A new version is available"
        color="primary"
        buttons={[
          { text: 'Refresh', handler: applyUpdate },
          { text: 'Dismiss', role: 'cancel' },
        ]}
        onDidDismiss={() => setNeedRefresh(false)}
      />
      <IonToast
        isOpen={installable}
        message="Add BluePages to your home screen"
        color="dark"
        buttons={[
          { text: 'Install', handler: () => void promptInstall() },
          { text: 'Later', role: 'cancel' },
        ]}
        onDidDismiss={() => setInstallable(false)}
      />
    </>
  );
}
